import { makeAutoObservable } from 'mobx';
import { fromPromise, type IPromiseBasedObservable } from 'mobx-utils';
import type PulseApi from '../../../api/PulseApi';
import type BundleStore from '../../bundles/store/BundleStore';
import type ProductStore from '../../products/store/ProductStore';
import type Promotion from './Promotion';
import Promotions from './Promotions';

export default class PromotionStore {
  constructor(
    private readonly api: PulseApi,
    private readonly bundleStore: BundleStore,
    private readonly productStore: ProductStore,
  ) {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  fetchAllPromotions(): IPromiseBasedObservable<Promotions> {
    const promises = Promise.all([
      this.api.promotions.getAllPromotions(),
      this.productStore.fetchAllProducts(),
      this.bundleStore.fetchAllBundles(),
    ]);

    return fromPromise(
      promises.then(([promotions, products, bundles]) => {
        return new Promotions(
          promotions,
          [...products.activeItems.items, ...bundles.activeItems.items],
          this.savePromotion,
        );
      }),
    );
  }

  private async savePromotion(promotion: Promotion) {
    switch (promotion.saveAction) {
      case 'create': {
        promotion.update(
          await this.api.promotions.createPromotion(promotion.toJSON()),
        );
        promotion.setSavingAction('update');
        return;
      }

      case 'delete': {
        await this.api.promotions.deletePromotion(promotion.id);
        return;
      }

      case 'update': {
        await this.api.promotions.updatePromotion(promotion.toJSON());
        return;
      }

      default: {
        throw new Error(`Unknown action ${promotion.saveAction}`);
      }
    }
  }
}
